import { useEffect, useState, useCallback, useRef } from 'react'
import { Plus, Trash2, MessageSquare, Search, Pencil, LogOut } from 'lucide-react'
import { useCpStore } from '@/stores/useCpStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { updateCpSession } from '@/lib/api'
import { cn } from '@/lib/utils'

interface CpSessionSidebarProps {
  onClose?: () => void
  onSessionSelect?: () => void
}

export function CpSessionSidebar({ onClose: _onClose, onSessionSelect }: CpSessionSidebarProps) {
  const {
    sessionId,
    sessions,
    isLoadingSessions,
    loadSessions,
    createNewSession,
    loadSessionFromDb,
    deleteSession,
  } = useCpStore()

  const { user, signOut } = useAuthStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [newSessionName, setNewSessionName] = useState('')
  const [showNewSession, setShowNewSession] = useState(false)
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)

  // Load sessions on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadSessions()
    }
  }, [loadSessions, user?.id])

  // Sort sessions by last_message_at (most recent first)
  const sortedSessions = [...sessions].sort((a, b) => {
    const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0
    const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0
    return dateB - dateA
  })

  const filteredSessions = sortedSessions.filter((session) =>
    (session.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCreateSession = useCallback(() => {
    createNewSession(newSessionName || undefined)
    setNewSessionName('')
    setShowNewSession(false)
    onSessionSelect?.()
  }, [createNewSession, newSessionName, onSessionSelect])

  const handleDeleteSession = useCallback((e: React.MouseEvent, sessionIdToDelete: string) => {
    e.stopPropagation()
    if (confirm('Delete this session? This cannot be undone.')) {
      deleteSession(sessionIdToDelete)
    }
  }, [deleteSession])

  const handleStartRename = useCallback((e: React.MouseEvent, sessionIdToRename: string, currentName: string) => {
    e.stopPropagation()
    setEditingSessionId(sessionIdToRename)
    setEditingName(currentName)
    setTimeout(() => editInputRef.current?.focus(), 0)
  }, [])

  const handleRenameSession = useCallback(async () => {
    if (!editingSessionId || !editingName.trim()) {
      setEditingSessionId(null)
      setEditingName('')
      return
    }

    try {
      await updateCpSession(editingSessionId, { name: editingName.trim() })
      loadSessions()
    } catch (error) {
      console.error('Failed to rename session:', error)
    }

    setEditingSessionId(null)
    setEditingName('')
  }, [editingSessionId, editingName, loadSessions])

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSession()
    } else if (e.key === 'Escape') {
      setEditingSessionId(null)
      setEditingName('')
    }
  }, [handleRenameSession])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No messages'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (days === 1) {
      return 'Yesterday'
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' })
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* New Session button */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-border">
        {showNewSession ? (
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Session name (optional)"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateSession()}
              className="w-full px-3 py-2 text-sm bg-muted/50 border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateSession}
                className="flex-1 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Create
              </button>
              <button
                onClick={() => setShowNewSession(false)}
                className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowNewSession(true)}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            New Session
          </button>
        )}
      </div>

      {/* Search */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-muted/50 border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingSessions ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            Loading sessions...
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            {searchQuery ? 'No sessions match your search' : 'No sessions yet'}
          </div>
        ) : (
          <div className="py-1">
            {filteredSessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => {
                  if (editingSessionId !== session.session_id) {
                    loadSessionFromDb(session.session_id)
                    onSessionSelect?.()
                  }
                }}
                className={cn(
                  "group flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors",
                  session.session_id === sessionId
                    ? "bg-muted/70"
                    : "hover:bg-muted/50"
                )}
              >
                <div className="flex-shrink-0 w-7 h-7 bg-muted rounded-md flex items-center justify-center">
                  <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  {editingSessionId === session.session_id ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      onBlur={handleRenameSession}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full text-sm font-medium bg-background border border-border rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  ) : (
                    <div className="text-sm font-medium truncate">
                      {session.name || 'Untitled'}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground truncate">
                    {formatDate(session.last_message_at)}
                  </div>
                </div>
                <button
                  onClick={(e) => handleStartRename(e, session.session_id, session.name || 'Untitled')}
                  className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Rename session"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <button
                  onClick={(e) => handleDeleteSession(e, session.session_id)}
                  className="flex-shrink-0 p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete session"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sign Out Footer */}
      <div className="flex-shrink-0 px-3 py-3 border-t border-border">
        <button
          onClick={signOut}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          <span className="flex-1 text-left truncate">
            Sign out
          </span>
        </button>
        {user?.email && (
          <div className="mt-1 px-3 text-xs text-muted-foreground truncate">
            {user.email}
          </div>
        )}
      </div>
    </div>
  )
}
